import React, { useEffect, useState } from 'react';
import { LysepAPI } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/Table";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import {
    Cpu,
    MemoryStick,
    RefreshCw,
    HardDrive,
    Database,
    AlertCircle,
    Activity,
    Info,
    Server,
    Layers
} from 'lucide-react';

const ResourcesTab = ({ serverId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);

    const fetchResources = async (forceScan = false) => {
        if (forceScan) setFetching(true);
        else setLoading(true);

        try {
            const endpoint = forceScan ? `/api/servers/${serverId}/resources/fetch` : `/api/servers/${serverId}/resources`;
            const resp = await (forceScan ? LysepAPI.post(endpoint) : LysepAPI.get(endpoint));

            if (resp.data.state) {
                setData(resp.data.data);
                if (forceScan) toast.success('Kaynak bilgileri güncellendi');
            } else {
                toast.error(resp.data.message || 'Veriler alınamadı');
            }
        } catch (err) {
            console.error('Resources fetch error:', err);
            toast.error('Bağlantı hatası');
        } finally {
            setLoading(false);
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, [serverId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Veriler yükleniyor...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="bg-blue-500/10 border border-blue-500/20 text-blue-400 p-4 rounded-xl flex items-center gap-3">
                <AlertCircle className="w-5 h-5" />
                <p>Kaynak bilgisi bulunamadı. Lütfen "Şimdi Tara" butonunu kullanarak verileri güncelleyin.</p>
            </div>
        );
    }

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const cpu = data.CPU?.[0];
    const getMetric = (cat, id, key) => data[cat]?.find(m => m.identifier === id && m.metric_key === key);

    const physicalMemUsed = getMetric('MEMORY', 'physical', 'used_bytes');
    const physicalMemPct = getMetric('MEMORY', 'physical', 'usage_pct');
    const swapMemUsed = getMetric('MEMORY', 'swap', 'used_bytes');
    const swapMemPct = getMetric('MEMORY', 'swap', 'usage_pct');

    const ProgressBar = ({ value, colorClass = "bg-blue-500", heightClass = "h-2" }) => (
        <div className={`w-full bg-white/10 rounded-full overflow-hidden ${heightClass}`}>
            <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${colorClass}`}
                style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
            ></div>
        </div>
    );

    const getProgressColor = (value) => {
        if (value > 90) return 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]';
        if (value > 70) return 'bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]';
        return 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]';
    };

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Header Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-orange-500" />
                        Sunucu Kaynakları
                    </h2>
                    <p className="text-gray-400 text-xs mt-1">Sunucu donanım ve kullanım detayları (Atomik Ölçümler)</p>
                </div>
                <Button
                    disabled={fetching}
                    onClick={() => fetchResources(true)}
                    variant="outline"
                    loading={fetching}
                    className="bg-[#1e1e1e] border-white/10 hover:border-white/20 text-white"
                >
                    {!fetching && <RefreshCw className="mr-2 h-4 w-4" />}
                    {fetching ? 'Taranıyor...' : 'Şimdi Tara'}
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* CPU Card */}
                <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-lg hover:border-white/20 transition-colors">
                    <div className="flex items-center gap-2 mb-4">
                        <Cpu className="w-5 h-5 text-blue-500" />
                        <h3 className="font-semibold text-white">CPU (İşlemci)</h3>
                    </div>

                    {cpu ? (
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Model</p>
                                <p className="text-sm font-medium text-white">{cpu.entity_metadata?.model || 'Bilinmiyor'}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Mimari</p>
                                    <p className="text-sm font-medium text-white">{cpu.sub_type}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Çekirdek</p>
                                    <p className="text-sm font-medium text-white">{cpu.entity_metadata?.cores || '-'}</p>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-white/10">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-xs font-bold text-gray-400">KULLANIM</span>
                                    <span className="text-xs font-bold text-white">{parseFloat(cpu.value).toFixed(1)}%</span>
                                </div>
                                <ProgressBar
                                    value={parseFloat(cpu.value)}
                                    colorClass={getProgressColor(cpu.value)}
                                    heightClass="h-2"
                                />
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic">CPU verisi yok.</p>
                    )}
                </div>

                {/* Memory Card */}
                <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 shadow-lg hover:border-white/20 transition-colors">
                    <div className="flex items-center gap-2 mb-4">
                        <MemoryStick className="w-5 h-5 text-purple-500" />
                        <h3 className="font-semibold text-white">Ram & Swap</h3>
                    </div>

                    <div className="space-y-6">
                        {physicalMemUsed && (
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-xs font-bold text-gray-400">FİZİKSEL BELLEK</span>
                                    <span className="text-xs font-bold text-white">
                                        {formatBytes(physicalMemUsed.value)} / {formatBytes(physicalMemUsed.entity_metadata?.total_bytes)}
                                    </span>
                                </div>
                                <ProgressBar
                                    value={physicalMemPct?.value || (physicalMemUsed.value / physicalMemUsed.entity_metadata?.total_bytes) * 100}
                                    colorClass="bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]"
                                    heightClass="h-3"
                                />
                            </div>
                        )}

                        {swapMemUsed && (
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-xs font-bold text-gray-400">SWAP (TAKAS ALANI)</span>
                                    <span className="text-xs font-bold text-white">
                                        {formatBytes(swapMemUsed.value)} / {formatBytes(swapMemUsed.entity_metadata?.total_bytes)}
                                    </span>
                                </div>
                                <ProgressBar
                                    value={swapMemPct?.value || (swapMemUsed.value / swapMemUsed.entity_metadata?.total_bytes) * 100}
                                    colorClass="bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Storage Table */}
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg">
                <div className="p-4 border-b border-white/10 bg-[#252525] flex items-center gap-2">
                    <Database className="w-5 h-5 text-gray-400" />
                    <h3 className="font-semibold text-white">Diskler ve Bağlama Noktaları (Filesystems)</h3>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Hedef (Mount)</TableHead>
                            <TableHead>Kaynak / Tip</TableHead>
                            <TableHead>Toplam</TableHead>
                            <TableHead>Kullanılan</TableHead>
                            <TableHead className="w-1/4">Durum</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {data.MOUNT?.map((mnt, idx) => {
                            if (mnt.metric_key !== 'usage_pct') return null;

                            const usage_val = parseFloat(mnt.value);
                            const used_bytes = data.MOUNT.find(m => m.identifier === mnt.identifier && m.metric_key === 'used_bytes')?.value;

                            const displayPath = mnt.identifier.length > 40
                                ? `...${mnt.identifier.slice(-37)}`
                                : mnt.identifier;

                            return (
                                <TableRow key={idx}>
                                    <TableCell className="font-mono text-gray-300" title={mnt.identifier}>
                                        <span className="border-b border-dotted border-gray-600 cursor-help">{displayPath}</span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="text-gray-300 truncate max-w-[150px]" title={mnt.entity_metadata?.source}>
                                                {mnt.entity_metadata?.source?.length > 20
                                                    ? `...${mnt.entity_metadata?.source?.slice(-17)}`
                                                    : mnt.entity_metadata?.source}
                                            </span>
                                            <span className="text-xs text-gray-500">{mnt.sub_type}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-gray-300">{formatBytes(mnt.entity_metadata?.total_bytes)}</TableCell>
                                    <TableCell className="text-gray-300">{formatBytes(used_bytes)}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1">
                                                <ProgressBar
                                                    value={usage_val}
                                                    colorClass={getProgressColor(usage_val)}
                                                    heightClass="h-1.5"
                                                />
                                            </div>
                                            <span className="text-xs font-bold text-gray-400 min-w-[3rem] text-right">
                                                %{usage_val.toFixed(1)}
                                            </span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            {/* Raw Drives */}
            <div className="flex flex-wrap gap-2">
                {data.DRIVE?.map((drive, idx) => (
                    <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] border border-white/10 rounded-lg text-sm text-gray-300 hover:border-blue-500/30 hover:bg-blue-500/5 transition-colors"
                    >
                        <HardDrive className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{drive.identifier}:</span>
                        <span className="text-gray-500">{formatBytes(drive.entity_metadata?.size_bytes)}</span>
                        <Badge variant="secondary" className="bg-white/5 text-gray-400 hover:bg-white/10">{drive.sub_type}</Badge>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ResourcesTab;
